import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AlertCircle, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '../../../../components/ui/Button';
import { PendingButton } from '../../../../components/ui/PendingButton';
import { NextTaskCard } from '../../components/NextTaskCard';
import { TaskRow } from '../../components/TaskRow';
import { HOME_TAB, HOME_TAB_LABELS } from '../../../../features/operator/operatorConstants';
import { operatorHomeTabChosen } from '../../../../features/operator/operatorSlice';
import { selectCurrentOperatorWarehouse } from '../../../../features/operator/operatorUtils';
import { fetchTasks } from '../../../../features/operatorTasks/operatorTasksThunks';
import { selectTray } from '../../../../features/operatorTasks/operatorTasksSlice';

/** One list of the tray, numbered by each task's place in it. */
const TaskList = ({ title, rows, testId, children }) =>
  rows.length > 0 && (
    <section className="space-y-2">
      <h2 className="text-sm font-bold text-text-secondary">{title}</h2>
      <ol data-testid={testId} className="space-y-3">
        {rows.map(({ task, position }) => (
          <li key={task.id}>
            <TaskRow task={task} position={position} />
          </li>
        ))}
      </ol>
      {children}
    </section>
  );

/**
 * Inicio's Tareas: «Siguiente», what the operator has on and what is left to
 * do. A failure says so in place; the stations of Trabajo libre still count
 * with what `me` said.
 */
export const HomeTasks = () => {
  const dispatch = useDispatch();
  const warehouse = useSelector(selectCurrentOperatorWarehouse);
  const tray = useSelector(selectTray);
  const { loading, error } = useSelector((state) => state.operatorTasks);
  const empty = tray && !tray.next && tray.mine.length === 0 && tray.open.length === 0;

  return (
    <div className="space-y-5">
      {error && (
        <div
          data-testid="tasks-error"
          className="space-y-3 rounded-2xl border border-border bg-white p-4 dark:bg-[#1a1a1a]"
        >
          <p className="flex items-start gap-2 text-sm text-ninja-dark dark:text-white">
            <AlertCircle size={18} className="mt-0.5 shrink-0 text-danger" />
            <span>
              <span className="block font-bold">No pudimos cargar tus tareas</span>
              <span className="block text-text-secondary">{error.message}</span>
            </span>
          </p>
          <PendingButton
            pendingOn={fetchTasks}
            variant="outline"
            size="lg"
            fullWidth
            icon={RefreshCw}
            loadingLabel="Reintentando…"
            onClick={() => dispatch(fetchTasks())}
            data-testid="tasks-retry"
          >
            Reintentar
          </PendingButton>
        </div>
      )}

      {!tray && loading && (
        <div data-testid="tasks-loading" className="flex justify-center py-6">
          <Loader2 size={28} className="animate-spin text-ninja-green" />
        </div>
      )}

      {empty && (
        <div
          data-testid="tasks-empty"
          className="space-y-3 rounded-2xl border border-border bg-white p-4 text-center dark:bg-[#1a1a1a]"
        >
          <p className="text-base font-semibold text-ninja-dark dark:text-white">
            No hay tareas pendientes en {warehouse?.name}
          </p>
          <Button
            variant="ghost"
            size="lg"
            fullWidth
            onClick={() => dispatch(operatorHomeTabChosen(HOME_TAB.FREE))}
            data-testid="home-empty-free"
          >
            Ir a {HOME_TAB_LABELS[HOME_TAB.FREE]}
          </Button>
        </div>
      )}

      {tray && !empty && (
        <>
          {tray.next && <NextTaskCard task={tray.next} />}
          <TaskList title="En curso" rows={tray.mine} testId="task-list-mine" />
          <TaskList title="Por hacer" rows={tray.open} testId="task-list-open">
            {tray.notShown > 0 && (
              <p className="text-sm text-text-secondary">
                Y {tray.notShown} más: las ves en {HOME_TAB_LABELS[HOME_TAB.FREE]}, en la cola de cada estación.
              </p>
            )}
          </TaskList>
        </>
      )}
    </div>
  );
};
